/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { isValidElement } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { getSecureRelForTarget, useEuiTheme, cloneElementWithCss } from '../../services';
import { useGeneratedHtmlId } from '../../services/accessibility';
import { validateHref } from '../../services/security/href_validator';
import { EuiText } from '../text';
import { EuiTitle } from '../title';
import { EuiBetaBadge } from '../badge/beta_badge';
import { EuiPanel } from '../panel';
import { EuiSpacer } from '../spacer';
import { EuiCardSelect } from './card_select';
import { euiCardBetaBadgeStyles, euiCardStyles, euiCardTextStyles } from './card.styles';
export var ALIGNMENTS = ['left', 'center', 'right'];

/**
 * Certain props are only allowed when the layout is vertical
 */

export var EuiCard = ({
  className,
  description,
  isDisabled: _isDisabled,
  title,
  titleElement = 'p',
  titleSize = 's',
  icon,
  image,
  children,
  footer,
  onClick,
  href,
  rel,
  target,
  textAlign = 'center',
  betaBadgeProps,
  layout = 'vertical',
  selectable,
  display,
  paddingSize = 'm',
  ...rest
}) => {
  const isHrefValid = !href || validateHref(href);
  const isDisabled = _isDisabled || !isHrefValid;
  const isClickable = !isDisabled && (onClick || href || selectable && !selectable.isDisabled);
  const euiThemeContext = useEuiTheme();
  const styles = euiCardStyles(euiThemeContext, paddingSize);
  const cardStyles = [styles.card.euiCard,
  // Text alignment should always be left when horizontal
  styles.card.aligned[layout === 'horizontal' ? 'left' : textAlign], isDisabled && styles.card.disabled];
  const contentStyles = [styles.content.euiCard__content, styles.content.layout[layout]];
  const mainStyles = [styles.main.euiCard__main, styles.main.layout[layout]];
  const textStyles = euiCardTextStyles(euiThemeContext);
  const textCSS = [textStyles.euiCard__text,
  // Text alignment should always be left when horizontal
  textStyles.aligned[layout === 'horizontal' ? 'left' : textAlign], isClickable && textStyles.interactive, isDisabled && textStyles.disabled];

  /**
   * For a11y, we simulate the same click that's provided on the title when clicking the whole card
   * without having to make the whole card a button or anchor tag.
   * *Card Accessibility: The redundant click event https://inclusive-components.design/cards/*
   */
  let link = null;
  const outerOnClick = e => {
    if (link && link !== e.target && !link.contains(e.target)) {
      link.click();
    }
  };
  if (layout === 'horizontal') {
    if (image || footer || textAlign !== 'center') {
      throw new Error('EuiCard: `layout="horizontal"` cannot be used in conjunction with `image`, `footer`, or `textAlign`.');
    }
  }
  const classes = classNames('euiCard', className);
  const ariaId = useGeneratedHtmlId();
  const ariaDesc = description ? `${ariaId}Description` : '';

  /**
   * Top area containing image, icon or both
   */

  let imageNode;
  if (image && layout === 'vertical') {
    if (isValidElement(image) || typeof image === 'string') {
      const imageStyles = [styles.image.euiCard__image, display === 'transparent' && styles.image.transparent];
      imageNode = <div className="euiCard__image" css={imageStyles}>
          {isValidElement(image) ? image : <img src={image} alt="" />}
        </div>;
    } else {
      imageNode = null;
    }
  }
  let iconNode;
  if (icon) {
    const iconStyles = [styles.icon.euiCard__icon, styles.icon.layout[layout], imageNode && styles.icon.withImage];
    iconNode = cloneElementWithCss(icon, {
      className: classNames(icon.props.className, 'euiCard__icon'),
      css: iconStyles
    });
  }
  let optionalCardTop;
  if (imageNode || iconNode) {
    const topStyles = [styles.top.euiCard__top, styles.top.layout[layout], isDisabled && styles.top.disabled];
    optionalCardTop = <div className="euiCard__top" css={topStyles}>
        {imageNode}
        {iconNode}
      </div>;
  }

  /**
   * Optional EuiBetaBadge
   */

  let optionalBetaBadge;
  let optionalBetaBadgeID = '';
  let optionalBetaCSS;
  if (betaBadgeProps?.label) {
    const betaStyles = euiCardBetaBadgeStyles(euiThemeContext, paddingSize);
    optionalBetaCSS = betaStyles.hasBetaBadge;
    const {
      anchorProps,
      ...cleanedBetaBadgeProps
    } = betaBadgeProps;
    const anchorCSS = [betaStyles.euiCard__betaBadgeAnchor, anchorProps?.css];
    const badgeCSS = [betaStyles.euiCard__betaBadge, betaBadgeProps?.css];
    optionalBetaBadgeID = `${ariaId}BetaBadge`;
    optionalBetaBadge = <EuiBetaBadge color={isDisabled && !betaBadgeProps.onClick && !betaBadgeProps.href ? 'subdued' : 'hollow'} {...cleanedBetaBadgeProps} css={badgeCSS} anchorProps={{
      ...anchorProps,
      css: anchorCSS
    }} id={optionalBetaBadgeID} />;

    // Increase padding size when there is a beta badge unless it's already determined
    // paddingSize = paddingSize || 'l';
  }

  /**
   * Optional selectable button
   */

  if (selectable && isDisabled && selectable.isDisabled === undefined) {
    selectable.isDisabled = isDisabled;
  }
  let optionalSelectButton;
  if (selectable) {
    optionalSelectButton = <>
        {paddingSize !== 'none' && <EuiSpacer size={paddingSize || 'm'} />}
        <EuiCardSelect aria-describedby={`${ariaId}Title ${ariaDesc}`} {...selectable} buttonRef={node => {
        link = node;
      }} />
      </>;
  }
  const TitleElement = titleElement;

  /**
   * Wraps the title with the link (<a>) or button.
   * This makes the title element a11y friendly and gets described by its content if its interactable.
   */

  let theTitle;
  if (!isDisabled && href) {
    theTitle = <a className="euiCard__titleAnchor" css={textCSS} onClick={onClick} href={href} target={target} aria-describedby={ariaDesc} rel={getSecureRelForTarget({
      href,
      target,
      rel
    })} ref={node => {
      link = node;
    }}>
        {title}
      </a>;
  } else if (isDisabled || onClick) {
    theTitle = <button className="euiCard__titleButton" css={textCSS} onClick={onClick} disabled={isDisabled} aria-describedby={`${optionalBetaBadgeID} ${ariaDesc}`} ref={node => {
      link = node;
    }}>
        {title}
      </button>;
  } else {
    theTitle = <span css={textCSS}>{title}</span>;
  }

  /**
   * Children and/or Description content
   */
  let optionalChildren;
  if (children) {
    const childrenStyles = [styles.euiCard__children];
    optionalChildren = <div css={childrenStyles}>{children}</div>;
  }
  let optionalDescription;
  if (description) {
    const descriptionStyles = [styles.euiCard__description];
    optionalDescription = <EuiText id={ariaDesc} size="s" css={descriptionStyles}>
        <p>{description}</p>
      </EuiText>;
  }

  /**
   * Footer content
   */
  let optionalFooter;
  if (layout === 'vertical' && footer) {
    const footerStyles = [styles.euiCard__footer];
    optionalFooter = <div css={footerStyles}>{footer}</div>;
  }
  return <EuiPanel element="div" className={classes} css={[...cardStyles, optionalBetaCSS]} onClick={isClickable ? outerOnClick : undefined} color={isDisabled ? 'subdued' : display} hasShadow={isDisabled || display ? false : true} hasBorder={display ? false : undefined} paddingSize={paddingSize} {...rest}>
      <div className="euiCard__main" css={mainStyles}>
        {optionalCardTop}

        <div className="euiCard__content" css={contentStyles}>
          <EuiTitle id={`${ariaId}Title`} className="euiCard__title" size={titleSize}>
            <TitleElement>{theTitle}</TitleElement>
          </EuiTitle>

          {optionalDescription}

          {optionalChildren}
        </div>

        {/* Beta badge should always be after the title/description but before any footer buttons */}
        {optionalBetaBadge}

        {optionalFooter}
      </div>

      {optionalSelectButton}
    </EuiPanel>;
};
EuiCard.propTypes = {
  /**
       * Change to "horizontal" if you need the icon to be left of the content.
       * Horizontal layouts cannot be used in conjunction with `image`, `footer`, or `textAlign`.
       */
  layout: PropTypes.oneOfType([PropTypes.oneOf(["vertical"]), PropTypes.oneOf(["horizontal"]).isRequired]),
  /**
       * Changes alignment of the title and description
       */
  textAlign: PropTypes.any,
  /**
       * Accepts any combination of elements
       */
  footer: PropTypes.node,
  /**
       * Accepts a url in string form or ReactElement for a custom image component
       */
  image: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]),
  /**
       * Cards are required to have at least a title and a description and/or children
       */
  title: PropTypes.any.isRequired,
  /**
       * Determines the title's heading element
       */
  titleElement: PropTypes.oneOf(["h2", "h3", "h4", "h5", "h6", "span", "p"]),
  /**
       * Determines the title's size, matching that of EuiTitle.
       * Though, card titles can't be too large or small relative to the description text.
       */
  titleSize: PropTypes.oneOf(["s", "xs"]),
  /**
       * Placed within a small EuiText `<p>` tag
       */
  description: PropTypes.any,
  /**
       * Accepts an `<EuiIcon>` node or `null`
       */
  icon: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.oneOf([null])]),
  /**
       * Custom children
       */
  children: PropTypes.node,
  /**
       * Use only if you want to forego a button in the footer and make the whole card clickable
       */
  onClick: PropTypes.oneOfType([PropTypes.func.isRequired, PropTypes.func.isRequired]),
  isDisabled: PropTypes.bool,
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  /**
       * Adds a badge to top of the card to label it as "Beta" or other non-GA state.
       * Accepts all the props of [EuiBetaBadge](#/display/badge#beta-badge-type), where `label` is required.
       */
  betaBadgeProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * Will apply an onclick to the badge itself
       */
    onClick: PropTypes.func,
    /**
       * Aria label applied to the onClick button
       */
    onClickAriaLabel: PropTypes.any,
    href: PropTypes.string,
    target: PropTypes.string,
    rel: PropTypes.string,
    /**
       * Supply an icon type if the badge should just be an icon
       */
    iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDenseVector", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
    /**
       * One word label like "Beta" or "Lab"
       */
    /**
       * One word label like "Beta" or "Lab"
       */
    label: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.string.isRequired]).isRequired]).isRequired,
    /**
       * Content for the tooltip
       */
    tooltipContent: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOfType([PropTypes.node, PropTypes.node.isRequired])]),
    /**
       * Custom position of the tooltip
       */
    tooltipPosition: PropTypes.oneOf(["top", "right", "bottom", "left"]),
    /**
       * Passes onto the span wrapping the badge
       */
    anchorProps: PropTypes.shape({
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any
    }),
    /**
       * Optional title will be supplied as tooltip title or title attribute
       * otherwise the label will be used
       */
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.string])]),
    /**
       * Accepts accent, subdued and hollow.
       */
    color: PropTypes.any,
    size: PropTypes.any,
    /**
       * Sets the `vertical-align` CSS property
       */
    alignment: PropTypes.any
  }),
  /**
       * Matches to the color property of EuiPanel. If defined, removes any border & shadow.
       * Leave as `undefined` to display as a default panel.
       * Selectable cards will always display as a default panel.
       */
  display: PropTypes.any,
  /**
       * Padding applied around the content of the card
       */
  paddingSize: PropTypes.any,
  /**
       * Adds a button to the bottom of the card to allow for in-place selection
       */
  selectable: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    children: PropTypes.node,
    /**
       * Make button a solid color for prominence
       */
    /**
       * Make button a solid color for prominence
       */
    fill: PropTypes.bool,
    /**
       * Any of the named color palette options.
       * **`'ghost'` is set for deprecation. Use EuiThemeProvide.colorMode = 'dark' instead.**
       */
    /**
       * Any of the named color palette options.
       * **`'ghost'` is set for deprecation. Use EuiThemeProvide.colorMode = 'dark' instead.**
       */
    color: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.oneOf(["ghost"])]),
    /**
       * Use size `s` in confined spaces
       */
    /**
       * Use size `s` in confined spaces
       */
    size: PropTypes.any,
    /**
       * `disabled` is also allowed
       */
    /**
       * `disabled` is also allowed
       */
    isDisabled: PropTypes.bool,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    buttonRef: PropTypes.any
  }),
  /**
       * Use a border style of card instead of shadow
       */
  hasBorder: PropTypes.bool
};
try {
  EuiCard.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/card/card.tsx',
    description: '',
    displayName: 'EuiCard',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      children: {
        defaultValue: null,
        description: 'Custom children',
        name: 'children',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      footer: {
        defaultValue: null,
        description: 'Accepts any combination of elements',
        name: 'footer',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      image: {
        defaultValue: null,
        description: 'Accepts a url in string form or ReactElement for a custom image component',
        name: 'image',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | ReactElement'
        }
      },
      textAlign: {
        defaultValue: {
          value: 'center'
        },
        description: 'Changes alignment of the title and description',
        name: 'textAlign',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"center" | "left" | "right"',
          value: [{
            value: '"center"'
          }, {
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      layout: {
        defaultValue: {
          value: 'vertical'
        },
        description: 'Change to "horizontal" if you need the icon to be left of the content.\n' + 'Horizontal layouts cannot be used in conjunction with `image`, `footer`, or `textAlign`.',
        name: 'layout',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"horizontal" | "vertical"',
          value: [{
            value: '"horizontal"'
          }, {
            value: '"vertical"'
          }]
        }
      },
      title: {
        defaultValue: null,
        description: 'Cards are required to have at least a title and a description and/or children',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      titleElement: {
        defaultValue: {
          value: 'p'
        },
        description: "Determines the title's heading element",
        name: 'titleElement',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span"',
          value: [{
            value: '"h2"'
          }, {
            value: '"h3"'
          }, {
            value: '"h4"'
          }, {
            value: '"h5"'
          }, {
            value: '"h6"'
          }, {
            value: '"p"'
          }, {
            value: '"span"'
          }]
        }
      },
      titleSize: {
        defaultValue: {
          value: 's'
        },
        description: "Determines the title's size, matching that of EuiTitle.\n" + "Though, card titles can't be too large or small relative to the description text.",
        name: 'titleSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "xs"',
          value: [{
            value: '"s"'
          }, {
            value: '"xs"'
          }]
        }
      },
      description: {
        defaultValue: null,
        description: 'Placed within a small EuiText `<p>` tag',
        name: 'description',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      icon: {
        defaultValue: null,
        description: 'Accepts an `<EuiIcon>` node or `null`',
        name: 'icon',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactElement<EuiIconProps, string | JSXElementConstructor<any>>'
        }
      },
      onClick: {
        defaultValue: null,
        description: 'Use only if you want to forego a button in the footer and make the whole card clickable',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'any'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '',
        name: 'isDisabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      href: {
        defaultValue: null,
        description: '',
        name: 'href',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      target: {
        defaultValue: null,
        description: '',
        name: 'target',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      betaBadgeProps: {
        defaultValue: null,
        description: 'Adds a badge to top of the card to label it as "Beta" or other non-GA state.\n' + 'Accepts all the props of [EuiBetaBadge](#/display/badge#beta-badge-type), where `label` is required.',
        name: 'betaBadgeProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiBetaBadgeProps'
        }
      },
      display: {
        defaultValue: null,
        description: 'Matches to the color property of EuiPanel. If defined, removes any border & shadow.\n' + 'Leave as `undefined` to display as a default panel.\n' + 'Selectable cards will always display as a default panel.',
        name: 'display',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"transparent" | "accent" | "primary" | "success" | "warning" | "danger" | "subdued" | "plain"',
          value: [{
            value: '"transparent"'
          }, {
            value: '"accent"'
          }, {
            value: '"primary"'
          }, {
            value: '"success"'
          }, {
            value: '"warning"'
          }, {
            value: '"danger"'
          }, {
            value: '"subdued"'
          }, {
            value: '"plain"'
          }]
        }
      },
      paddingSize: {
        defaultValue: {
          value: 'm'
        },
        description: 'Padding applied around the content of the card',
        name: 'paddingSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      selectable: {
        defaultValue: null,
        description: 'Adds a button to the bottom of the card to allow for in-place selection',
        name: 'selectable',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(DisambiguateSet<EuiButtonPropsForAnchor, EuiButtonPropsForButton> & EuiButtonProps & { ...; } & ButtonHTMLAttributes<...> & { ...; }) | (DisambiguateSet<...> & ... 3 more ... & { ...; })'
        }
      },
      hasBorder: {
        defaultValue: null,
        description: 'Use a border style of card instead of shadow',
        name: 'hasBorder',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/card/card.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}